import { useEffect, useReducer, useState } from 'react';
import { ThemeToggle } from './theme/ThemeToggle.js';
import { pathToView, useApp, type View } from './store.js';
import { Login } from './pages/Login.jsx';
import { Setup } from './pages/Setup.jsx';
import { bootVault } from './bootstrap.js';
import { vault } from './sync/vault.js';
import { Aurebesh } from './mo/Aurebesh.js';
import { Icon, type IconName } from './mo/Icon.js';
import { navigateToLink, vaultFileUrl } from './navigation.js';
import { MoHome } from './mo/MoHome.js';
import { MoDashboard } from './mo/MoDashboard.js';
import { NoteEditor } from './editor/index.jsx';
import { GraphView, LocalGraphPanel } from './graph/index.jsx';
import { Help } from './mo/Help.jsx';
import {
  BacklinksPane,
  FolderTree,
  HistoryPanel,
  PropertiesPanel,
  QuickSwitcher,
  SearchPane,
  TagPane,
} from './views/index.jsx';
import { CanvasView } from './canvas/index.jsx';
import { BaseView } from './bases/index.jsx';
import { openDailyNote, SettingsDialog, ShortcutRow, NotePrinter, exportNoteToPdf } from './features/index.jsx';
import { Home } from './views/Home.jsx';
import { ShareDialog } from './features/ShareDialog.jsx';
import { AssistantPanel } from './assistant/AssistantPanel.jsx';
import type { AuthStatus } from '@rbv/shared';

export function App() {
  const authState = useApp((s) => s.authState);
  const setAuth = useApp((s) => s.setAuth);
  const setReadOnlyMounts = useApp((s) => s.setReadOnlyMounts);

  /*
   * The skin attribute on <html>, applied here rather than in the dashboard.
   *
   * On the document root and in App, it covers every view: the deck, a note, the graph, the boot
   * screen. Set from the dashboard it vanished the moment he opened a note, which is the half of
   * the app he asked to have skinned in the first place.
   */
  const skin = useApp((s) => s.skin);
  useEffect(() => {
    const root = document.documentElement;
    if (skin === 'beskar') root.setAttribute('data-skin', 'beskar');
    else root.removeAttribute('data-skin');
  }, [skin]);

  useEffect(() => {
    fetch('/api/auth/status')
      .then((r) => r.json())
      .then((s: AuthStatus) => {
        // An older server omits readOnlyMounts; default to ["vantage"] rather
        // than treating every note as writable.
        setReadOnlyMounts(s.readOnlyMounts ?? ['vantage']);
        if (s.needsSetup) setAuth('setup');
        else if (!s.authenticated) setAuth('login');
        else setAuth('ready', s.username);
      })
      .catch(() => {
        // Offline PWA start: if we have local data, go straight in.
        setAuth('ready');
      });
  }, [setAuth, setReadOnlyMounts]);

  if (authState === 'loading') {
    /*
     * The MO mark rather than a spinner, at his request.
     *
     * Matched deliberately to the static splash in `index.html`: same background, same mark, same
     * position, so the handover from "HTML has painted" to "React is running" is invisible. A
     * spinner appearing where a logo was is the flash that makes a boot feel slow even when it is
     * not.
     */
    return (
      <div className="boot">
        <div className="boot-mark" aria-hidden="true" />
        <p className="boot-label">MO OS</p>
      </div>
    );
  }
  if (authState === 'setup') return <Setup />;
  if (authState === 'login') return <Login />;
  return <Shell />;
}

function Shell() {
  const view = useApp((s) => s.view);
  const restoreView = useApp((s) => s.restoreView);
  const sidebarOpen = useApp((s) => s.sidebarOpen);
  const setSidebarOpen = useApp((s) => s.setSidebarOpen);
  const sidebarTab = useApp((s) => s.sidebarTab);
  const setSidebarTab = useApp((s) => s.setSidebarTab);
  const rightOpen = useApp((s) => s.rightOpen);
  const setRightOpen = useApp((s) => s.setRightOpen);
  const editorMode = useApp((s) => s.editorMode);
  const setEditorMode = useApp((s) => s.setEditorMode);
  const setQuickSwitcherOpen = useApp((s) => s.setQuickSwitcherOpen);
  const assistantOpen = useApp((s) => s.assistantOpen);
  const setAssistantOpen = useApp((s) => s.setAssistantOpen);
  const syncStatus = useApp((s) => s.syncStatus);
  const keyboardOpen = useApp((s) => s.keyboardOpen);
  const openView = useApp((s) => s.openView);
  const openNote = useApp((s) => s.openNote);
  const pinnedIds = useApp((s) => s.pinnedIds);
  const togglePin = useApp((s) => s.togglePin);
  const [booted, setBooted] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    void bootVault().then(() => {
      setBooted(true);
      const v = pathToView(location.pathname);
      useApp.getState().bootTabs(v);
      // MCP export_note deep-link: /n/<id>?print=1 auto-opens the print (Save as
      // PDF) sheet once the note's doc has streamed in.
      if (v.kind === 'note' && new URLSearchParams(location.search).get('print') === '1') {
        let tries = 0;
        const t = setInterval(() => {
          tries += 1;
          if (vault.getEntry(v.id) && vault.noteContent(v.id)) {
            clearInterval(t);
            exportNoteToPdf(v.id);
          } else if (tries > 60) {
            clearInterval(t);
          }
        }, 150);
      }
    });
  }, [restoreView]);

  useEffect(() => {
    const onPop = () => restoreView(pathToView(location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [restoreView]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === 'o' || e.key === 'p' || e.key === 'k')) {
        e.preventDefault();
        setQuickSwitcherOpen(true);
      } else if (mod && e.key === 'g') {
        e.preventDefault();
        openView({ kind: 'graph' });
      } else if (mod && e.key === 'e') {
        e.preventDefault();
        setEditorMode(useApp.getState().editorMode === 'reading' ? 'live' : 'reading');
      } else if (mod && e.key === 'j') {
        e.preventDefault();
        useApp.getState().setAssistantOpen(!useApp.getState().assistantOpen);
      } else if (mod && e.key === 'w') {
        const st = useApp.getState();
        if (st.tabs.length > 1) {
          e.preventDefault();
          st.closeTab(st.activeTab);
        }
      } else if (mod && /^[1-9]$/.test(e.key)) {
        const st = useApp.getState();
        if (st.tabs.length > 1) {
          e.preventDefault();
          st.setActiveTab(Math.min(Number(e.key) - 1, st.tabs.length - 1));
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setQuickSwitcherOpen, openView, setEditorMode]);

  if (!booted) {
    return (
      <div className="auth-page">
        <div className="spinner" />
      </div>
    );
  }

  const noteId = view.kind === 'note' ? view.id : null;

  /** Graph/panel node clicks: notes open in the editor, canvases in the
   * canvas view, attachments in a new tab. */
  const openFromGraph = (id: string) => {
    const entry = vault.getEntry(id);
    if (!entry) return;
    if (entry.kind === 'file') window.open(vaultFileUrl(entry.path), '_blank', 'noopener');
    else if (entry.kind === 'canvas') openView({ kind: 'canvas', id });
    else openNote(id);
  };
  const title =
    view.kind === 'note' || view.kind === 'canvas'
      ? vault.titleOf(view.id)
      : view.kind === 'graph'
        ? 'Graph'
        : view.kind === 'base'
          ? 'Base'
          : view.kind === 'dashboard'
            ? 'Dashboard'
            : 'MO OS';

  return (
    /*
      `data-view` lets the dashboard hide the notes sidebar until it is asked for. He
      reported "Whey I have 2 Menus": on desktop `.sidebar` is a permanent 280px column,
      so the deck's own rail sat beside it and the Files tree was always taking width
      the deck wanted. His words: "this is an extra thing appear when I want". The ☰
      button still opens it, and every other view is unaffected.
    */
    <div className="app-shell" data-view={view.kind}>
      <aside className={`sidebar${sidebarOpen ? ' open' : ''}`}>
        <div className="side-tabs">
          {(['files', 'search', 'tags'] as const).map((t) => (
            <button
              key={t}
              className={`icon-btn${sidebarTab === t ? ' active' : ''}`}
              title={t}
              onClick={() => setSidebarTab(t)}
            >
              {/* Was emoji. See mo/Icon.tsx: a colour glyph rendered by the OS carries a
                  different weight and shape on every device, which is most of why the notes app
                  and the deck read as two products. */}
              <Icon name={t === 'files' ? 'folder' : t === 'search' ? 'search' : 'tag'} size={17} />
            </button>
          ))}
          <span style={{ flex: 1 }} />
          <button className="icon-btn" title="Settings" onClick={() => setSettingsOpen(true)}>
            <Icon name="settings" size={17} />
          </button>
        </div>
        <div className="side-body">
          {sidebarTab === 'files' && <FolderTree />}
          {sidebarTab === 'search' && <SearchPane />}
          {sidebarTab === 'tags' && <TagPane />}
        </div>

        {/*
          THE RAIL'S PLATE. Frame furniture, and the small touch he asked for: a helmet sigil and
          a stencil strip closing the column, the way a bulkhead is marked at its foot.

          It carries NOTHING and is not a control, which is why it is a span rather than a button
          and why it is hidden from assistive technology. Every other mark in this column means
          something, so the one that does not has to be unmistakably decoration.
        */}
        <span className="side-plate" aria-hidden="true">
          <Icon name="sigil" size={18} />
          <Aurebesh seed="rail-plate" length={5} size={9} />
        </span>
      </aside>
      {(sidebarOpen || rightOpen) && (
        <div
          className="backdrop"
          onClick={() => {
            setSidebarOpen(false);
            setRightOpen(false);
          }}
        />
      )}

      <main className="main-pane">
        <div className="topbar">
          {/*
            LINE ICONS, not emoji.
            
            The topbar shipped with emoji: a house, a calendar showing the 17th, a spider web, a
            pink brain. Against the plated deck they read as stickers on an instrument panel, and
            they were never consistent with the icon set the deck itself uses. Emoji also render as
            a different typeface per platform, so nothing about their weight or size could be
            controlled. Same `Icon` set as the deck now, at one size, following currentColor.
          */}
          <button className="icon-btn" title="Menu" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <Icon name="menu" size={17} />
          </button>
          {/* The dashboard is the landing surface now, so the home affordance goes
              there. Upstream's Home is still one click away, from the dashboard's
              own "Open the vault". */}
          <button
            className={`icon-btn${view.kind === 'dashboard' ? ' active' : ''}`}
            title="Dashboard"
            onClick={() => openView({ kind: 'dashboard' })}
          >
            <Icon name="home" size={17} />
          </button>
          <span className="title">{title}</span>
          <span className={`sync-dot ${syncStatus}`} title={`sync: ${syncStatus}`} />
          <ThemeToggle />
          <button className="icon-btn" title="Daily note" onClick={() => void openDailyNote()}>
            <Icon name="agenda" size={17} />
          </button>
          <button
            className="icon-btn"
            title="Quick switcher (⌘O)"
            onClick={() => setQuickSwitcherOpen(true)}
          >
            <Icon name="search" size={17} />
          </button>
          <button
            className={`icon-btn${view.kind === 'graph' ? ' active' : ''}`}
            title="Graph view"
            onClick={() => openView({ kind: 'graph' })}
          >
            <Icon name="graph" size={17} />
          </button>
          {/* MO OS brain. Without this the /mo route was unreachable from the UI. */}
          {/* The guide. Reachable from the bar rather than buried, because a manual you
              have to remember the URL for is one you do not open. */}
          <button
            className={`icon-btn${view.kind === 'help' ? ' active' : ''}`}
            title="How MO OS works"
            onClick={() => openView({ kind: 'help' })}
          >
            <Icon name="help" size={17} />
          </button>
          <button
            className={`icon-btn${view.kind === 'mo' ? ' active' : ''}`}
            title="MO OS: what you owe and what you are waiting on"
            onClick={() => openView({ kind: 'mo' })}
          >
            <Icon name="brain" size={17} />
          </button>
          <button
            className={`icon-btn${assistantOpen ? ' active' : ''}`}
            title="Assistant (⌘J)"
            onClick={() => setAssistantOpen(!assistantOpen)}
          >
            <Icon name="spark" size={17} />
          </button>
          <button
            className="icon-btn"
            title="New tab"
            onClick={() => useApp.getState().newTab()}
          >
            <Icon name="plus" size={17} />
          </button>
          {noteId && (
            <button
              className="icon-btn"
              title={`Mode: ${editorMode}`}
              onClick={() =>
                setEditorMode(
                  editorMode === 'live' ? 'source' : editorMode === 'source' ? 'reading' : 'live',
                )
              }
            >
              <Icon
                name={editorMode === 'live' ? 'edit' : editorMode === 'source' ? 'code' : 'reading'}
                size={17}
              />
            </button>
          )}
          {noteId && (
            <button
              className={`icon-btn${pinnedIds.includes(noteId) ? ' active' : ''}`}
              title={pinnedIds.includes(noteId) ? 'Unpin' : 'Pin to Home'}
              onClick={() => togglePin(noteId)}
            >
              {/* One glyph for both states: `active` on the button already carries the
                  difference, and a filled-versus-hollow pair in two different characters was the
                  only place in the app where a state change also changed the shape. */}
              <Icon name="star" size={17} />
            </button>
          )}
          {noteId && (
            <button
              className="icon-btn"
              title="Export to PDF"
              onClick={() => exportNoteToPdf(noteId)}
            >
              <Icon name="download" size={16} />
            </button>
          )}
          {noteId && (
            <button className="icon-btn" title="Share note" onClick={() => setShareOpen(true)}>
              <Icon name="share" size={16} />
            </button>
          )}
          {noteId && (
            <button
              className={`icon-btn${rightOpen ? ' active' : ''}`}
              title="Backlinks & info"
              onClick={() => setRightOpen(!rightOpen)}
            >
              <Icon name="link" size={16} />
            </button>
          )}
        </div>

        <TabStrip />

        <div className="content-area">
          {view.kind === 'dashboard' && <MoDashboard />}
          {view.kind === 'empty' && <Home />}
          {view.kind === 'mo' && <MoHome />}
          {view.kind === 'note' && <NoteView id={view.id} heading={view.heading} blockId={view.blockId} />}
          {view.kind === 'graph' && <GraphView mode="global" onOpenNote={openFromGraph} />}
          {view.kind === 'help' && <Help />}
          {view.kind === 'canvas' && (
            <CanvasView canvasId={view.id} onOpenNote={(id) => openNote(id)} />
          )}
          {view.kind === 'base' && <BaseView baseId={view.id} onOpenNote={(id) => openNote(id)} />}
        </div>
        {keyboardOpen && noteId && <ShortcutRow />}
      </main>

      {noteId && (
        <aside className={`right-panel${rightOpen ? ' open' : ''}`}>
          <PropertiesPanel noteId={noteId} />
          <LocalGraphPanel noteId={noteId} onOpenNote={openFromGraph} />
          <BacklinksPane noteId={noteId} />
          <HistoryPanel noteId={noteId} />
        </aside>
      )}

      <QuickSwitcher />
      <AssistantPanel />
      <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      {shareOpen && noteId && <ShareDialog idOrPath={noteId} onClose={() => setShareOpen(false)} />}
      <NotePrinter />
    </div>
  );
}

function NoteView({ id, heading, blockId }: { id: string; heading?: string; blockId?: string }) {
  const editorMode = useApp((s) => s.editorMode);
  const [, bump] = useReducer((x: number) => x + 1, 0);
  const [timedOut, setTimedOut] = useState(false);
  const ytext = vault.ytext(id);
  const entry = vault.getEntry(id);

  // On a cold start the meta doc and note docs stream in asynchronously —
  // poll briefly until this note's doc has loaded, then re-render. Give up
  // after a few seconds so a stale/bad /n/<id> URL doesn't spin forever.
  useEffect(() => {
    if (ytext && entry) return;
    setTimedOut(false);
    let waited = 0;
    const t = setInterval(() => {
      waited += 100;
      if (vault.ytext(id) && vault.getEntry(id)) {
        clearInterval(t);
        bump();
      } else if (waited >= 6000) {
        clearInterval(t);
        setTimedOut(true);
      }
    }, 100);
    return () => clearInterval(t);
  }, [id, ytext, entry]);

  // Release presence for this note when the view unmounts / note changes.
  useEffect(() => () => vault.releaseAwareness(id), [id]);

  if (entry?.deleted) {
    return <div className="muted" style={{ padding: 20 }}>Note not found.</div>;
  }
  if (!ytext || !entry) {
    return timedOut ? (
      <div className="muted" style={{ padding: 20 }}>Note not found.</div>
    ) : (
      <div className="spinner" />
    );
  }
  return (
    <NoteEditor
      noteId={id}
      ytext={ytext}
      awareness={vault.awareness(id)}
      mode={editorMode}
      onNavigate={(t) => void navigateToLink({ ...t, fromId: id })}
      scrollToHeading={heading}
      scrollToBlock={blockId}
    />
  );
}

/** Open editor tabs. Hidden while there is only one tab (the single-note UX is
 * unchanged); a horizontally-scrollable pill row on mobile. */
function TabStrip() {
  const tabs = useApp((s) => s.tabs);
  const activeTab = useApp((s) => s.activeTab);
  const setActiveTab = useApp((s) => s.setActiveTab);
  const closeTab = useApp((s) => s.closeTab);
  const newTab = useApp((s) => s.newTab);
  if (tabs.length <= 1) return null;

  const label = (v: View): string =>
    v.kind === 'note' || v.kind === 'canvas'
      ? vault.titleOf(v.id) || 'Untitled'
      : v.kind === 'graph'
        ? 'Graph'
        : v.kind === 'base'
          ? 'Base'
          : v.kind === 'dashboard'
            ? 'Dashboard'
            : 'Home';
  const icon = (v: View): IconName =>
    v.kind === 'graph'
      ? 'graph'
      : v.kind === 'canvas'
        ? 'canvas'
        : v.kind === 'base'
          ? 'table'
          : v.kind === 'empty' || v.kind === 'dashboard'
            ? 'home'
            : 'notes';

  return (
    <div className="tab-strip" role="tablist">
      {tabs.map((v, i) => (
        <div
          key={i}
          className={`tab${i === activeTab ? ' active' : ''}`}
          role="tab"
          aria-selected={i === activeTab}
          title={label(v)}
          onClick={() => setActiveTab(i)}
          onAuxClick={(e) => {
            if (e.button === 1) {
              e.preventDefault();
              closeTab(i);
            }
          }}
        >
          <span className="tab-icon">
            <Icon name={icon(v)} size={14} />
          </span>
          <span className="tab-label">{label(v)}</span>
          <button
            className="tab-close"
            title="Close tab (⌘W)"
            onClick={(e) => {
              e.stopPropagation();
              closeTab(i);
            }}
          >
            ×
          </button>
        </div>
      ))}
      <button className="tab-new" title="New tab" onClick={() => newTab()}>
        ＋
      </button>
    </div>
  );
}

